import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, User, Mail, AlertCircle } from "lucide-react";
import { profileService, UserProfileData } from "@/services/profileService";
import { useProfileAutoSave } from "@/hooks/useProfileAutoSave";

// Profile form schema
const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name must be less than 100 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name must be less than 100 characters"),
});

type FormValues = z.infer<typeof profileSchema>;

const ProfileForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const { user, refreshProfile } = useAuth();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  // Auto-save functionality
  const { triggerSave, triggerSaveImmediate, isSaving, lastSaved } =
    useProfileAutoSave({
      onSave: async (data) => {
        if (!user?.id) {
          toast({
            variant: "destructive",
            title: "Save failed",
            description:
              "User ID not found. Please refresh the page and try again.",
          });
          return false;
        }

        // Ensure we have both firstName and lastName
        if (!data.firstName || !data.lastName) {
          toast({
            variant: "destructive",
            title: "Save failed",
            description: "Both first name and last name are required.",
          });
          return false;
        }

        const result = await profileService.updateUserProfile(user.id, {
          firstName: data.firstName!,
          lastName: data.lastName!,
        });

        if (!result.success) {
          toast({
            variant: "destructive",
            title: "Failed to save profile",
            description: result.error || "An unexpected error occurred.",
          });
          return false;
        }

        // Update profileData to reflect the saved values and prevent unnecessary re-saves
        setProfileData((prev) =>
          prev
            ? {
                ...prev,
                firstName: data.firstName,
                lastName: data.lastName,
              }
            : null,
        );

        return true;
      },
    });

  // Load profile data on mount
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      try {
        const result = await profileService.getUserProfile(user.id);
        if (result.success && result.data) {
          setProfileData(result.data);
          form.reset({
            firstName: result.data.firstName,
            lastName: result.data.lastName,
          });
        } else {
          toast({
            variant: "destructive",
            title: "Failed to load profile",
            description: result.error || "An unexpected error occurred.",
          });
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        toast({
          variant: "destructive",
          title: "Failed to load profile",
          description: "An unexpected error occurred.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user?.id, form, toast]);

  // Watch form changes and trigger auto-save
  const watchedValues = form.watch();
  useEffect(() => {
    if (!isLoading && profileData) {
      // Only trigger save if values have actually changed
      const hasChanged =
        watchedValues.firstName !== profileData.firstName ||
        watchedValues.lastName !== profileData.lastName;

      // Ensure we have both values before triggering save
      if (hasChanged && watchedValues.firstName && watchedValues.lastName) {
        triggerSave({
          firstName: watchedValues.firstName,
          lastName: watchedValues.lastName,
        });
      }
    }
  }, [watchedValues, triggerSave, isLoading, profileData]);

  // Handle avatar upload
  const handleAvatarUpload = async (file: File) => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          "User ID not found. Please refresh the page and try again.",
      });
      return;
    }

    setIsUploading(true);

    try {
      const result = await profileService.uploadAvatar(user.id, file);

      if (!result.success) {
        throw new Error(result.error || "Upload failed");
      }

      // Refresh profile data to get new avatar URL
      const updatedProfile = await profileService.getUserProfile(user.id);
      if (updatedProfile.success && updatedProfile.data) {
        setProfileData(updatedProfile.data);
      }

      // Refresh auth context
      await refreshProfile();

      toast({
        title: "Avatar updated",
        description: "Your profile picture has been uploaded successfully.",
      });
    } catch (error: any) {
      console.error("Avatar upload error:", error);
      toast({
        variant: "destructive",
        title: "Failed to upload avatar",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleAvatarUpload(file);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id) return;

    try {
      const result = await profileService.removeAvatar(user.id);

      if (!result.success) {
        throw new Error(result.error || "Failed to remove avatar");
      }

      // Update local state
      if (profileData) {
        setProfileData({ ...profileData, avatarUrl: null });
      }

      // Refresh auth context
      await refreshProfile();

      toast({
        title: "Avatar removed",
        description: "Your profile picture has been removed.",
      });
    } catch (error: any) {
      console.error("Remove avatar error:", error);
      toast({
        variant: "destructive",
        title: "Failed to remove avatar",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="flex space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          const values = form.getValues();
          if (values.firstName && values.lastName) {
            triggerSaveImmediate({
              firstName: values.firstName,
              lastName: values.lastName,
            });
          }
        }}
      >
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-medium">My Profile</h3>
              {isSaving && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Auto-saving...
                </div>
              )}
              {lastSaved && !isSaving && (
                <div className="text-sm text-muted-foreground">
                  Saved {lastSaved.toLocaleTimeString()}
                </div>
              )}
            </div>

            {/* Avatar Section */}
            <div className="space-y-4">
              <FormLabel>Profile Picture</FormLabel>
              <div className="flex items-center gap-6">
                <div className="h-20 w-20 rounded-full border border-input flex items-center justify-center bg-muted overflow-hidden">
                  {profileData?.avatarUrl ? (
                    <img
                      src={profileData.avatarUrl}
                      alt="Profile picture"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isUploading}
                      onClick={() =>
                        document.getElementById("avatar-upload")?.click()
                      }
                    >
                      {isUploading ? (
                        <>
                          <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload Photo
                        </>
                      )}
                    </Button>
                    {profileData?.avatarUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAvatar}
                        disabled={isUploading}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <Input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                    disabled={isUploading}
                    onClick={(e) => ((e.target as HTMLInputElement).value = "")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Recommended: Square image, max 5MB. PNG or JPG.
                  </p>
                </div>
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter your last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <Button type="submit" variant="outline" size="sm">
                Save
              </Button>
            </div>

            {/* Email Field (Read-only) */}
            <FormItem>
              <FormLabel>Email Address</FormLabel>
              <div className="space-y-2">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={profileData?.email || ""}
                    disabled
                    className="pl-10 bg-muted"
                  />
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4" />
                  <span>
                    To edit your email address, please reach out to our support
                    team.
                  </span>
                </div>
              </div>
            </FormItem>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};

export default ProfileForm;
